import React, { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import { formatDistanceToNow } from "date-fns";

const ENDPOINT = "https://chat-ojsz.onrender.com"

export default function ChatRoom({ room, onLeave }) {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [userCount, setUserCount] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const [socket, setSocket] = useState(null);
  const messagesEndRef = useRef(null);

  // Connect to socket and join room on component mount
  useEffect(() => {
    const newSocket = io(ENDPOINT);
    setSocket(newSocket);

    newSocket.emit("joinRoom", { room });

    // Socket event listeners
    newSocket.on("message", (msg) => {
      setMessages((prevMessages) => [...prevMessages, msg]);
    });

    newSocket.on("chatHistory", (history) => {
      setMessages(history);
    });

    newSocket.on("roomUsers", ({ count }) => {
      setUserCount(count);
    });

    newSocket.on("chatCleared", () => {
      setMessages([]);
    });

    // Clean up on unmount
    return () => {
      newSocket.emit("leaveRoom", { room });
      newSocket.disconnect();
    };
  }, [room]);

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (message.trim() && socket) {
      socket.emit("chatMessage", {
        room,
        message,
        replyTo: replyTo?._id || null,
      });
      setMessage("");
      setReplyTo(null);
    }
  };

  const handleClearChat = () => {
    if (socket) {
      socket.emit("clearChat", room);
    }
  };

  const handleLeaveChat = () => {
    if (socket) {
      socket.emit("leaveRoom", { room });
    }
    onLeave();
  };

  const cancelReply = () => setReplyTo(null);

  return (
    <div className="flex flex-col w-full max-w-4xl h-[80vh] bg-gray-800 rounded-xl shadow-2xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-900 p-4 border-b border-gray-700 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-blue-300">{room}</h2>
          <p className="text-xs text-gray-400">Active Users: {userCount}</p>
        </div>
        <div className=" flex gap-2">
          <button
            onClick={handleClearChat}
            className="hidden bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
          >
            Clear Chat
          </button>
          <button
            onClick={handleLeaveChat}
            className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
          >
            Leave
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto bg-gray-900">
        {messages.length > 0 ? (
          messages.map((msg, index) => (
            <div
              key={index}
              className={`mb-4 ${
                msg.socketId === "system"
                  ? "flex justify-center"
                  : msg.socketId === socket?.id
                  ? "flex justify-end"
                  : "flex justify-start"
              }`}
            >
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  msg.socketId === "system"
                    ? "bg-gray-700 text-gray-300 text-center text-sm"
                    : msg.socketId === socket?.id
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-white"
                }`}
              >
                {/* Reply reference */}
                {msg.replyTo && (
                  <div className="text-sm opacity-80 border-l-2 pl-2 mb-2 border-gray-400">
                    <p className="italic text-xs">
                      {msg.replyTo.socketId === socket?.id
                        ? "You"
                        : msg.replyTo.socketId === "system"
                        ? "System"
                        : "Anonymous"}
                    </p>
                    <p className="truncate">{msg.replyTo.message}</p>
                  </div>
                )}
                
                {/* Message content */}
                <div className="flex flex-col">
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="font-semibold text-sm">
                      {msg.socketId === "system"
                        ? "System"
                        : msg.socketId === socket?.id
                        ? "You"
                        : "Anonymous"}
                    </span>
                    {msg.timestamp && (
                      <span className="text-xs opacity-60 ml-2">
                        {formatDistanceToNow(new Date(msg.timestamp), {
                          addSuffix: true,
                        })}
                      </span>
                    )}
                  </div>
                  <p className="break-words">{msg.message}</p>
                </div>

                {/* Reply button */}
                {msg.socketId !== "system" && (
                  <button
                    onClick={() => setReplyTo(msg)}
                    className="text-xs mt-2 opacity-60 hover:opacity-100"
                  >
                    Reply
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            No messages yet. Start the conversation!
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply indicator */}
      {replyTo && (
        <div className="bg-gray-700 p-2 px-4 flex justify-between items-center border-t border-gray-600">
          <div className="flex items-center">
            <span className="text-xs text-gray-400 mr-2">Replying to:</span>
            <span className="text-sm truncate">{replyTo.message}</span>
          </div>
          <button
            onClick={cancelReply}
            className="text-gray-400 hover:text-white ml-2"
          >
            ✕
          </button>
        </div>
      )}

      {/* Message Input */}
      <form onSubmit={sendMessage} className="p-4 bg-gray-800 border-t border-gray-700">
        <div className="flex">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 p-3 rounded-l-lg bg-gray-700 border-gray-600 text-white focus:outline-none"
          />
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 px-6 rounded-r-lg text-white font-medium transition-colors duration-200"
            disabled={!message.trim()}
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
}